

import { namespace } from "vuex-class"
import { Component, Vue, Watch, Prop, Emit } from "vue-property-decorator"

// @ts-ignore
import vSelect from "vue-select"
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import { Case, Field } from "@/types"

import api from "@/services/api"

import HubRecordViewService from "@/services/hubRecordViewService"

import SkeletonLoader from "../SkeletonLoader.vue"
import CaseList from "@/components/CaseList/CaseList.vue"
import { CurrentPageDetails, GetCasesForViewRequest } from "@/types/paginatedRequest"
import { CasesTable, CasesTables } from "@/types/case"

const caseStore = namespace("cases")
const setup = namespace("setup")

@Component({
	components: {
		"scrollbar": VuePerfectScrollbar,
		"case-list": CaseList,
		"skeleton": SkeletonLoader,
		"v-select": vSelect
	}
})
export default class RecordView extends Vue {
	@Prop()
	value: any;

	@Prop()
	edit!: boolean

	@caseStore.Action("detailsLoad")
	private detailsLoad!: () => void;

	@setup.Action loadFieldsIfUnloaded!: () => Promise<Field[]>

	views: any[] = [];

	activeView: any = null;
	loadingCases: boolean = true;
	errorLoading: boolean = false;
	errorUpdating: boolean = false;
	displayRecords: Case[] = [];
	private casesTables: CasesTables | null = null;
	private displayRecordTables: CasesTable[] = [];
	hubDashboardService: HubRecordViewService = new HubRecordViewService();

	private pageNumber: number = 1;
	private pageSize: number = 20;

	public async mounted () {
		await this.loadFieldsIfUnloaded()
		this.onEditChanged()
	}

	public beforeDestroy () {
		this.closeHubConnection()
	}

	public async rowClick (item: any) {
		this.$router.push({ path: `/cases/${item.caseNumber}` })
	}

	@Watch("edit")
	private async onEditChanged () {
		if (this.edit) {
			this.setEditMode()
		} else {
			this.setViewMode()
		}
	}

	private async setEditMode () {
		this.closeHubConnection()

		this.views = await api.caseViewsGet()
	}

	private async setViewMode (searchTerm: string = "") {
		this.loadingCases = true
		this.errorLoading = false

		try {
			if (this.value && this.value.viewId) {
				if (!this.activeView || this.activeView.id !== this.value.viewId) {
					api.caseViewsGet()
						.then(views => {
							this.views = views

							if (this.views) {
								this.activeView = this.views.find(v => v.id === this.value.viewId)
							}
						})
				}

				this.casesTables = await api.caseList(this.value.viewId, this.pageNumber, this.pageSize, this.casesTables?.id ?? "", searchTerm)
				this.displayRecordTables = this.casesTables.casesTables
			}
		} catch (err) {
			this.errorLoading = true
		}

		this.loadingCases = false
		await this.openHubConnection()
	}

	private async openHubConnection () {
		if (!this.value || !this.value.viewId) {
			return
		}

		try {
			const connection = await this.hubDashboardService.openConnection(this.value.viewId)

			if (connection) {
				connection.on("recordView", (data: Case[]) => {
					this.displayRecords = data
					this.errorUpdating = false
				})

				connection.on("recordViewError", (err: String) => {
					console.log(err)
					this.errorUpdating = true
				})
			}
		} catch (err) {
			this.errorUpdating = true
		}
	}

	private closeHubConnection () {
		if (!this.hubDashboardService) {
			return
		}

		this.hubDashboardService.closeConnection()
	}

	@Emit("input")
	private viewChanged (viewId: string) {
		return { ...this.value, viewId }
	}

	private async loadNextPage (details: CurrentPageDetails) {
		this.loadingCases = true
		this.errorLoading = false

		try {
			if (!!this.value && !!this.value.viewId) {
				this.pageNumber = details.pageNumber ?? 1
				this.pageSize = details.pageSize ?? 20

				const request: GetCasesForViewRequest = {
					viewId: this.value.viewId,
					id: this.casesTables?.id ?? "",
					page: this.pageNumber,
					pageSize: this.pageSize,
					searchTerm: details.searchTerm,
					timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					sortBy: details.sortBy,
					sortDesc: details.sortDesc,
					sortDataType: details.sortDataType,
					tableId: details.tableId
				}
				const tableId = details.tableId ? details.tableId : details.value
				const casesTables = await api.caseListTable(request)
				const casesTable = casesTables.casesTables.firstOrDefault((c: CasesTable) => c.value === tableId)
				const tableToUpdate = this.displayRecordTables.firstOrDefault((c: CasesTable) => c.value === tableId)

				if (tableToUpdate) {
					tableToUpdate.totalRecords = casesTable.totalRecords
					tableToUpdate.cases = [...casesTable.cases]
				}
			}
		} catch (err) {
			console.error(err)
			this.errorLoading = true
		}

		this.loadingCases = false
	}

	private async reloadBySearch (searchTerm: string = ""): Promise<void> {
		this.pageNumber = 1
		await this.setViewMode(searchTerm)
	}
}
